import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Modal } from "stories/components";
import { Input, Radio } from "stories/forms";

// Above this many portfolios the list gets a search field and scrolls.
const SEARCH_THRESHOLD = 8;

const TITLE_ID = "target-portfolio-title";
const HELP_ID = "target-portfolio-help";
const ERROR_ID = "target-portfolio-error";

const useTitle = (action) => {
  const { t } = useTranslation();
  if (action === "purchase") {
    return t("target_portfolio_title_purchase", "Mihin salkkuun osto kirjataan?");
  }
  if (action === "sale") {
    return t("target_portfolio_title_sale", "Mihin salkkuun myynti kirjataan?");
  }
  return t("target_portfolio_title_other", "Mihin salkkuun tapahtuma kirjataan?");
};

// Mounted only while the dialog is open, so its effects run after the Modal has rendered
// the radios and after reactstrap has focused the dialog itself.
const DialogBody = ({ portfolios, companies, selectedId, setSelectedId, attempts, showError }) => {
  const { t } = useTranslation();
  const listRef = useRef(null);
  const [search, setSearch] = useState("");

  const visible = useMemo(() => {
    const query = search.trim().toLowerCase();
    return query ? portfolios.filter((p) => String(p.name || "").toLowerCase().includes(query)) : portfolios;
  }, [portfolios, search]);

  // Focus is on the first radio when the dialog opens, and moves back there after "Jatka"
  // without a choice.
  useEffect(() => {
    const first = listRef.current?.querySelector('input[type="radio"]');
    if (first) {
      first.focus();
    }
  }, [attempts]);

  const companyName = (id) => (companies || []).find((c) => c?.id === id)?.name;

  const choices = visible.map((p) => {
    const company = companyName(p.company);
    return {
      id: `target-portfolio-${p.id}`,
      value: String(p.id),
      label: (
        <span className="d-flex justify-content-between">
          <span className="mr-3">{p.name}</span>
          <span className="text-muted">
            {[p.currency_label, company].filter(Boolean).join(" · ")}
          </span>
        </span>
      ),
    };
  });

  const many = portfolios.length > SEARCH_THRESHOLD;

  return (
    <>
      <p id={HELP_ID}>
        {t("target_portfolio_help", "Aktiivisia salkkuja on {{count}}. Valitse yksi, niin lomake avautuu.", { count: portfolios.length })}
      </p>
      {many && (
        <Input
          name="target_portfolio_search"
          label={t("target_portfolio_search", "Hae salkkua")}
          aria-label={t("target_portfolio_search", "Hae salkkua")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      )}
      <div
        ref={listRef}
        role="radiogroup"
        aria-labelledby={TITLE_ID}
        aria-describedby={showError ? `${HELP_ID} ${ERROR_ID}` : HELP_ID}
        aria-required="true"
        style={many ? { maxHeight: "50vh", overflowY: "auto" } : undefined}
        className={many ? "mt-3" : undefined}
      >
        <Radio
          name="target_portfolio"
          choices={choices}
          checkedChoice={selectedId ? String(selectedId) : undefined}
          onChange={(e) => setSelectedId(Number(e.target.value))}
          aria-invalid={showError ? "true" : "false"}
          aria-describedby={showError ? ERROR_ID : undefined}
        />
      </div>
      {showError && (
        <div id={ERROR_ID} className="text-danger mt-2">
          <i className="fas fa-exclamation-circle mr-2" aria-hidden="true" />
          {t("target_portfolio_required", "Valitse salkku, johon tapahtuma kirjataan.")}
        </div>
      )}
    </>
  );
};

/**
 * Asks which portfolio a new record goes to when several portfolios are active (#1409).
 * Only the active portfolios, in name order, with no preselection. Rendered once by
 * TargetPortfolioProvider; views use useTargetPortfolio().choose(action).
 */
const TargetPortfolioDialog = ({ isOpen, action, portfolios, companies, onConfirm, onCancel }) => {
  const { t } = useTranslation();
  const title = useTitle(action);
  const [selectedId, setSelectedId] = useState(null);
  const [attempts, setAttempts] = useState(0);
  // The provider gives each opening a new key, so the choice and the error start empty.
  const [showError, setShowError] = useState(false);

  const handleContinue = () => {
    const chosen = (portfolios || []).find((p) => p.id === selectedId);
    if (!chosen) {
      setShowError(true);
      setAttempts((n) => n + 1);
      return;
    }
    onConfirm(chosen);
  };

  // reactstrap keeps Tab and Shift+Tab inside the dialog and closes it on Esc. Its own focus
  // return is off: the provider returns focus to the opener on cancel, and after "Jatka"
  // the form that opens takes it.
  return (
    <Modal
      isOpen={isOpen}
      title={<span id={TITLE_ID}>{title}</span>}
      labelledBy={TITLE_ID}
      toggleModal={onCancel}
      closeModal={onCancel}
      returnFocusAfterClose={false}
      buttons={[
        <Button key="cancel" color="secondary" onClick={onCancel}>
          {t("target_portfolio_cancel", "Peruuta")}
        </Button>,
        <Button key="continue" color="primary" onClick={handleContinue}>
          {t("target_portfolio_continue", "Jatka")}
        </Button>,
      ]}
    >
      {isOpen && (
        <DialogBody
          portfolios={portfolios || []}
          companies={companies}
          selectedId={selectedId}
          setSelectedId={(id) => {
            setSelectedId(id);
            setShowError(false);
          }}
          attempts={attempts}
          showError={showError}
        />
      )}
    </Modal>
  );
};

export default TargetPortfolioDialog;
